/* eslint-disable react-hooks/rules-of-hooks */
import { css } from '@emotion/react';
import { withProperties } from '$/utils/type';
import React, {
  FormEventHandler,
  FunctionComponent,
  ReactElement,
  RefObject,
  SyntheticEvent,
} from 'react';
import ReactDOM from 'react-dom';
import {
  Controller,
  FormProvider,
  FormState,
  useForm,
  useFormContext,
  Message,
  FieldValues,
  UseFormReturn,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ErrorMessage } from '@hookform/error-message';
import * as yup from 'yup';

import { useId } from 'react-id-generator';
import Label from 'semantic-ui-react/dist/commonjs/elements/Label';
import tw from 'twin.macro';
import Button from '../Button';
import DirtyFormContext from '$/components/Router/DirtyFormContext';
import { useDebouncedCallback } from 'use-debounce';
import styled, { StyledComponent } from '@emotion/styled';
import { getFieldId } from '$/utils/e2e/form';
import { ToastContext, ToastMessageInput, ToastMessageType } from '$/components/Toast/Toast';
import { Icon } from 'semantic-ui-react';
import PermissionedLink from '$/components/Router/PermissionedLink';
import { LinkProps } from '@reach/router';
import { ButtonT } from '../ThemeContext';
import { useHasChangedShallow, useHasChangedDeep, useDeepCompareMemo } from '$/hooks/useHasChanged';
import { logger } from '$/utils/logger';
import DangerouslySetInnerHTML from '../EditableInput/DangerouslySetInnerHTML';
import { MediaRefFragment } from '$/GraphQL/schema/schema';

interface RawFieldProps {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (value: any) => void;
  onFocus?: () => void;
  onBlur?: (e: SyntheticEvent, value?: string | null) => void;
  component: JSX.Element;
  type?: string;
  errorMessage?: string;
  noClassName?: boolean;
  className?: string;
  inputRef?: RefObject<unknown>;
  defaultValue?: unknown;
  valueName?: string;
  shouldUnregister?: boolean;
  isArrayValue?: boolean;
}

interface FieldProps extends RawFieldProps {
  errors?: Errors;
  type?: string;
  renderError?: (message: Message) => React.ReactElement;
  renderErrorGap?: boolean;
}

interface AccordionTitleProps {
  className?: string;
  title: string | JSX.Element;
  alwaysOpen?: boolean;
  children?: React.ReactNode;
}
interface FormComponent extends React.FC<FormProps<FieldValues>> {
  AccordionTitle: React.FC<AccordionTitleProps>;
  Field: StyledComponent<FieldProps>;
  StopNonHubspotForm: React.FC;
  RawField: StyledComponent<RawFieldProps>;
  FieldError: React.FC<FieldErrorProps>;
  FieldLabel: StyledComponent<FieldLabelProps>;
  ErrorMessage: React.FC<FormErrorMessageProps>;
  Status: React.FC<{ className?: string }>;
  Link: React.FC<
    Omit<
      React.PropsWithoutRef<LinkProps<any>> & React.RefAttributes<HTMLAnchorElement>,
      'onClick'
    > & {
      onClick?: (_e: React.MouseEvent<HTMLAnchorElement>) => void;
      skipDirtyForm?: boolean;
    }
  >;
}

yup.setLocale({
  mixed: {
    default: 'This field is invalid.',
    required: 'This field is required.',
  },
  string: {
    email: 'Please provide a valid email address.',
    url: 'Please provide a valid URL.',
  },
});

export enum SubmitMode {
  onSubmitButton = 'onSubmitButton',
  onChangeDebounced = 'onChangeDebounced',
}

type Value = string | number | boolean | DefaultValues;

interface DefaultValues {
  [key: string]: Value | Value[];
}

interface Errors {
  [key: string]: string;
}

interface FormOptions {
  submitMode?: SubmitMode;
  onSubmit: (event?: React.FormEvent<HTMLFormElement>) => Promise<void>;
  formId: string;
  imageUpload: { [key: string]: boolean };
  setImageUpload: React.Dispatch<{ [key: string]: boolean }>;
  addPendingUpload?: (fieldName: string, fn: () => Promise<void>) => void;
  removePendingUpload?: (fieldName: string) => void;
  processPendingUploads?: () => Promise<void>;
  onFormChange?: (name: string) => void;
  onFormBlur?: (name: string) => void;
  processUploadsComplete?: (mediaRef: MediaRefFragment, updatedMediaRef: MediaRefFragment) => void;
}

export const FormOptionsContext = React.createContext<FormOptions>({} as FormOptions);

export type FormOnSubmitFn<Values extends FieldValues> = (
  values: any,
  defaultValues: Values,
  formState?: FormState<Values>,
  formHandlers?: UseFormReturn<Values>
) => Promise<void>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface FormProps<Values extends FieldValues> {
  className?: string;
  validationSchema?: yup.AnyObjectSchema | yup.Lazy<yup.AnyObjectSchema>;
  defaultValues?: Values;
  mode?: 'onBlur' | 'onSubmit' | 'onChange' | undefined;
  showUnsavedChangesDialog?: boolean;
  showErrorOnToast?: boolean;
  resetAfterSave?: boolean;
  shouldUnregister?: boolean;
  resetStateAfterSubmit?: boolean;
  resetToNewDefault?: boolean;
  /**  Specify when onSubmit is called. */
  submitMode?: SubmitMode;
  onChange?: (name: string) => void;
  onBlur?: (name: string) => void;
  onFocus?: (e: React.FormEvent<HTMLFormElement>) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLFormElement> | undefined;
  onSubmit?: FormOnSubmitFn<Values>;
  children?: React.ReactNode;
}

export class SubmitError extends Error {
  errors: Errors;

  constructor(errors: Errors) {
    super('SubmitError');
    this.name = 'SubmitError'; // (2)
    this.errors = errors;
  }
}

interface FieldErrorProps {
  name: string;
  errors?: Errors;
  className?: string;
  render?: (message: Message) => React.ReactElement;
  renderErrorGap?: boolean;
}

export function handleSubmitErrors(
  e: SubmitError,
  formHandlers: UseFormReturn,
  showOnToast?: (message: ToastMessageInput) => string
): void {
  if (e instanceof SubmitError && e.errors) {
    Object.keys(e.errors).map((name) => {
      if (name === '_' && showOnToast) {
        showOnToast({ message: e.errors[name], type: ToastMessageType.Error });
      }
      formHandlers.setError(name, { type: 'error', message: e.errors[name] });
    });
  } else {
    formHandlers.setError('_', { type: 'error', message: e.toString() });
  }
}

const Form: FormComponent = ({
  mode = 'onSubmit',
  validationSchema,
  submitMode = SubmitMode.onSubmitButton,
  resetAfterSave = true,
  resetToNewDefault = false,
  defaultValues,
  showUnsavedChangesDialog,
  showErrorOnToast,
  shouldUnregister,
  resetStateAfterSubmit = true,
  onBlur: onFormBlur,
  onChange: onFormChange,
  onFocus: onFormFocus,
  ...props
}) => {
  const defaultValuesMemoized = useDeepCompareMemo(defaultValues ?? {});
  const { show } = React.useContext(ToastContext);
  const resolver = validationSchema ? yupResolver(validationSchema) : undefined;
  const formHandlers = useForm({
    mode,
    resolver,
    defaultValues,
    shouldUnregister: shouldUnregister ?? true, // use this options to ignore fields from defaultValues but do not render (eg. defaultValues has 3 fields but we render only 2 fields so data of onSubmit will have only 2 fields)
  });

  const { changeVersion: dirtyFieldsVersion } = useHasChangedDeep([
    formHandlers.formState.dirtyFields,
  ]);
  const { changeVersion: defaultValuesVersion, hasChanged: hasDefaultChanged } = useHasChangedDeep([
    defaultValuesMemoized,
  ]);

  React.useEffect(() => {
    // Don't reset if dirty
    // The reality is this should only reset if any of the fields this form cares
    // about have changed - not any field in defaultValues

    // Reset form to default if form does not have any dirty fields
    if (!hasDefaultChanged && resetToNewDefault) return;

    if (
      (resetToNewDefault && defaultValuesMemoized && hasDefaultChanged) ||
      (resetAfterSave && defaultValuesMemoized && !formHandlers.formState.isDirty) ||
      (formHandlers.formState.isDirty &&
        Object.keys(formHandlers.formState.dirtyFields).length === 0)
    ) {
      formHandlers.reset(defaultValuesMemoized);
    }
  }, [
    resetAfterSave,
    formHandlers,
    formHandlers.formState.isDirty,
    defaultValuesVersion,
    dirtyFieldsVersion,
    defaultValuesMemoized,
    hasDefaultChanged,
    resetToNewDefault,
  ]);

  // create state for uploading image
  const [imageUpload, setImageUpload] = React.useState<{ [key: string]: boolean }>({});

  // queue for pending image uploads
  const pendingUploadsRef = React.useRef<{ [key: string]: () => Promise<void> }>({});

  const addPendingUpload = React.useCallback((fieldName: string, fn: () => Promise<void>) => {
    pendingUploadsRef.current[fieldName] = fn;
  }, []);

  const removePendingUpload = React.useCallback((fieldName: string) => {
    delete pendingUploadsRef.current[fieldName];
  }, []);

  const processPendingUploads = React.useCallback(async (): Promise<void> => {
    const uploads = Object.values(pendingUploadsRef.current);
    pendingUploadsRef.current = {};
    await Promise.all(uploads.map((fn) => fn()));
  }, []);

  const processUploadsComplete = React.useCallback(
    (mediaRef: MediaRefFragment, updatedMediaRef: MediaRefFragment) => {
      // Update any form fields that contain the old URL
      const oldUrl = mediaRef.url;
      const oldImageUrl = mediaRef.imageUrl;
      const currentValues = formHandlers.getValues();
      let hasUpdates = false;
      const updatedValues = { ...currentValues };

      Object.keys(currentValues).forEach((fieldName) => {
        const fieldValue = currentValues[fieldName];

        if (
          fieldName.toLowerCase().endsWith('mediaref') &&
          fieldValue &&
          typeof fieldValue === 'object'
        ) {
          if (fieldValue.url === oldUrl || fieldValue.imageUrl === oldImageUrl) {
            // Replace entire MediaRef object with new one
            updatedValues[fieldName] = updatedMediaRef;
            hasUpdates = true;
          }
        } else if (typeof fieldValue === 'string' && fieldValue.includes(oldUrl)) {
          updatedValues[fieldName] = fieldValue.replace(
            new RegExp(oldUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
            updatedMediaRef.imageUrl
          );
          hasUpdates = true;
        }
      });

      if (hasUpdates) {
        // Update form values
        Object.keys(updatedValues).forEach((fieldName) => {
          if (updatedValues[fieldName] !== currentValues[fieldName]) {
            formHandlers.setValue(fieldName, updatedValues[fieldName], { shouldDirty: true });
          }
        });
      }
    },
    [formHandlers]
  );

  const onSubmit = React.useCallback(
    async (event?: React.FormEvent<HTMLFormElement>): Promise<void> => {
      // this part is for stopping parent forms to trigger their submit
      if (event) {
        // sometimes not true, e.g. React Native
        if (typeof event.preventDefault === 'function') {
          event.preventDefault();
        }
        if (typeof event.stopPropagation === 'function') {
          // prevent any outer forms from receiving the event too
          event.stopPropagation();
        }
      }

      return formHandlers.handleSubmit(async (values) => {
        try {
          // Process any pending uploads before form submission
          await processPendingUploads();

          // Update values with any fields that were modified during upload processing
          Object.keys(values).forEach((fieldName) => {
            if (fieldName.toLowerCase().endsWith('mediaref')) {
              const currentValue = formHandlers.getFieldState(fieldName).isDirty
                ? formHandlers.getValues(fieldName)
                : values[fieldName];
              values[fieldName] = currentValue;
            }
          });

          if (props.onSubmit) {
            await props.onSubmit(
              values,
              defaultValuesMemoized,
              formHandlers.formState,
              formHandlers
            );
          }

          //
          // Resetting the form was causing the Wizard to zero out when we didn't want it to
          // and doesn't this cause registration forms to zero out before we navigate away
          //
          // Why did we need this??  If some forms need it we should make it an option via a prop
          //
          //const formValues = formHandlers.getValues();
          //formHandlers.reset(formValues);

          if (resetStateAfterSubmit) {
            // reset other form state but keep defaultValues and form values
            formHandlers.reset(undefined, { keepValues: true });
          }
        } catch (error) {
          const e = error as Error;

          console.warn('SUBMIT ERROR: ', { isSubmitError: e instanceof SubmitError, e });

          if (e instanceof SubmitError && e.errors) {
            handleSubmitErrors(e, formHandlers, showErrorOnToast ? show : undefined);
          }
        }
      })(event);
    },
    [
      formHandlers,
      processPendingUploads,
      props,
      resetStateAfterSubmit,
      defaultValuesMemoized,
      showErrorOnToast,
      show,
    ]
  );

  const formRef = React.useRef<HTMLFormElement>(null);
  const [formId] = useId();

  // read dirty form context
  const { setForms } = React.useContext(DirtyFormContext);

  const hasDirtyOrSubmittedChanged = useHasChangedShallow([
    formHandlers.formState.isDirty,
    formHandlers.formState.isSubmitting,
  ]);

  // update dirty form context when dirty state changes
  React.useEffect(() => {
    if (!showUnsavedChangesDialog || !hasDirtyOrSubmittedChanged) return;

    // update dirty state in context
    setForms((current) => {
      return {
        ...current,
        [formId]: {
          isDirty: formHandlers.formState.isDirty,
          isSubmitting: formHandlers.formState.isSubmitting,
          submit: onSubmit,
          reset: formHandlers.reset,
          trigger: formHandlers.trigger,
        },
      };
    });
  }, [
    formHandlers.formState.isDirty,
    formHandlers.formState.isSubmitting,
    formHandlers.reset,
    formHandlers.trigger,
    formId,
    hasDirtyOrSubmittedChanged,
    onSubmit,
    setForms,
    showUnsavedChangesDialog,
  ]);

  const onFocus = React.useCallback<FormEventHandler<HTMLFormElement>>(
    (e) => {
      onFormFocus && onFormFocus(e);
    },
    [onFormFocus]
  );

  React.useEffect(() => {
    // form unmount
    return (): void => {
      setForms((forms) => {
        const newState = { ...forms };
        delete newState[formId];
        return newState;
      });
    };
  }, [formId, setForms]);

  return (
    <FormOptionsContext.Provider
      value={{
        submitMode,
        onSubmit,
        formId,
        imageUpload,
        addPendingUpload,
        removePendingUpload,
        processPendingUploads,
        onFormBlur,
        onFormChange,
        setImageUpload,
        processUploadsComplete,
      }}
    >
      <FormProvider {...formHandlers}>
        <form {...props} onFocus={onFocus} id={formId} onSubmit={onSubmit} ref={formRef}>
          {props.children}
        </form>
      </FormProvider>
    </FormOptionsContext.Provider>
  );
};

Form.FieldError = ({ errors, name, render, renderErrorGap, className }): JSX.Element | null => {
  const contextErrors = useFormContext().formState.errors;

  if (!errors?.[name] && !contextErrors[name] && renderErrorGap) {
    return (
      <div
        className='hidden md:block'
        css={css`
          height: 52.32px;
        `}
      />
    );
  }

  return (
    <ErrorMessage
      errors={errors}
      name={name}
      render={({ message }) => {
        message = message || contextErrors[name]?.root?.message?.toString() || '';
        if (render) {
          return render(message);
        }
        return (
          <Label color='red' basic pointing className={`error-message ${className}`}>
            <DangerouslySetInnerHTML css={tw`text-color-error`} markdownString={message} />
          </Label>
        );
      }}
    />
  );
};

interface FieldLabelProps {
  title: ReactElement | string;
  className?: string;
  large?: boolean;
  required?: boolean;
  children?: React.ReactNode;
}

Form.FieldLabel = styled<React.FC<FieldLabelProps>>(
  ({ title, children, large, required, className }) => {
    return (
      <div
        className={`field-label ${className}`}
        css={[
          tw`flex text-color-input-field mr-1e `,
          large ? tw`text-xl mb-1e` : tw`text-md mb-1/2e`,
          css`
            .required {
              ${tw`ml-1 text-lg text-red-500`}
            }
          `,
        ]}
      >
        <span className='inline-flex'>
          {title}
          {required && <span className='required'>*</span>}
          {children}
        </span>
      </div>
    );
  }
)``;
Form.FieldLabel.displayName = 'FieldLabel';

// Using styled so we can ${Form.Field} in css``
Form.Field = styled<React.FC<FieldProps>>(
  ({ name, className, component, errors, renderError, renderErrorGap, ...props }) => {
    if (errors) {
      console.error(`${name} has error `, errors);
    }

    return (
      <div
        id={getFieldId(name)}
        css={tw`mb-1e w-full`}
        className={`flex flex-col ${props.type === 'hidden' ? 'hidden' : ''} ${className}`}
      >
        <Form.RawField
          {...props}
          noClassName
          name={name}
          component={component}
          onChange={props.onChange}
        />
        {props.type !== 'hidden' && (
          <Form.FieldError
            render={renderError}
            renderErrorGap={renderErrorGap}
            errors={errors}
            name={name}
          />
        )}
      </div>
    );
  }
)``;
Form.Field.displayName = 'Field';

// Using styled so we can ${Form.RawField} in css``
Form.RawField = styled<React.FC<RawFieldProps>>(
  ({ component, noClassName, defaultValue, shouldUnregister, isArrayValue, ...props }) => {
    const { onSubmit, submitMode, onFormChange, onFormBlur } = React.useContext(FormOptionsContext);
    const formHandlers = useFormContext();

    const onSubmitDebounced = useDebouncedCallback(
      async () => {
        submitMode === SubmitMode.onChangeDebounced && (await onSubmit());
      },
      500,
      { trailing: true }
    );

    const { control } = formHandlers;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFocus = (): any => {
      logger.debug('ANIM', 'RawField onFocus ', props.name);

      props.onFocus && props.onFocus();
      onFormChange && onFormChange(props.name ?? '');
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onChange = (args: any[]): any => {
      const e = args?.[0];
      const data = args?.[1];
      let value;

      if (isArrayValue && Array.isArray(args)) {
        value = args;
      } else if (typeof args === 'string') {
        value = args;
      } else if (data) {
        value = data.value;
      } else if (Array.isArray(args)) {
        if (e === undefined || e === null) return null;
        value = e?.target ? e.target?.value : e;
      } else {
        value = args;
      }

      // For some reason Controller onFocus doesn't fire onFocus of the controlled input element
      // Instead use onChange to surmize the input box has focus and tell the form.  We would want
      // to tell the form Focus continues to be on the input element during onChange anyway
      onFormChange && onFormChange(props.name ?? '');

      onSubmitDebounced();
      props.onChange && props.onChange(value);
      return value;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onBlur = (args: any[]): any => {
      const e = args?.[0];
      const data = args?.[1];
      let value;

      if (data) {
        value = data.value;
      } else if (Array.isArray(args)) {
        if (e === undefined || e === null) return null;
        value = e?.target ? e.target?.value : e;
      } else {
        value = args;
      }

      onFormBlur && onFormBlur(props.name ?? '');

      props.onBlur && props.onBlur(e, value);
      return value;
    };

    if (noClassName) {
      // Usually used by Form.Field as it already used className on the parent
      // element - removing className now allows us to use the className defined
      // in component
      delete props.className;
    }

    return (
      <Controller
        shouldUnregister={shouldUnregister}
        name={props.name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }): JSX.Element => {
          const disabled = component?.props?.disabled ?? field.disabled;
          const formFieldProps = {
            ...props,
            ...field,
            disabled,
            onBlur: (e: any) => {
              field.onBlur();
              onBlur(e);
            },
            onChange: (value: any) => {
              field.onChange(value);
              onChange(value);
            },
            onFocus: () => {
              component?.props.onFocus?.();
              onFocus();
            },
            ref: undefined,
          };
          return React.cloneElement(component, formFieldProps);
        }}
      />
    );
  }
)``;
Form.RawField.displayName = 'RawField';

export interface SubmitButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  dirtyText?: string;
  cleanText?: string;
  containerId?: string;
  className?: string;
  disabled?: boolean;
  hideOnClean?: boolean;
  button?: FunctionComponent;
  submitting?: boolean;
  theme?: ButtonT;
}

const SubmitButton: React.FC<SubmitButtonProps> = ({
  containerId,
  hideOnClean,
  submitting,
  ...props
}) => {
  const { formState } = useFormContext();
  const { formId, imageUpload } = React.useContext(FormOptionsContext);

  // is there any image uploading
  const isImageUploading = imageUpload && Object.values(imageUpload).includes(true);

  const { isSubmitting, isDirty, isValid, isSubmitted } = formState;

  if ((!isDirty || Object.keys(formState.dirtyFields).length === 0) && hideOnClean) {
    return null;
  }

  const disabled =
    props.disabled || isSubmitting || (!isDirty && !isValid && isSubmitted) || isImageUploading;

  const hasErrors = isDirty && !isValid && isSubmitted && !isSubmitting;

  const children = (
    <Button.submit
      dirty={isDirty}
      submitting={isSubmitting || Boolean(submitting)}
      disabled={disabled}
      formId={formId}
      {...props}
      className={`submit-button ${hasErrors ? 'bg-color-error' : ''} ${props.className}`}
    />
  );

  if (containerId) {
    const el = document.getElementById(containerId);

    return el ? ReactDOM.createPortal(children, el) : null;
  } else {
    return children;
  }
};

interface FormErrorMessageProps {
  className?: string;
  message?: JSX.Element | string;
}

Form.ErrorMessage = ({
  className,
  message = 'Please check required fields and errors in this form.',
}): JSX.Element | null => {
  const {
    formState: { errors },
  } = useFormContext();

  let errorMessage: JSX.Element | string = '';
  if (Object.entries(errors).length > 0) {
    errorMessage = errors._ && errors._.message ? String(errors._.message) : message;
  }

  if (!errorMessage || (typeof errorMessage === 'string' && errorMessage.length === 0)) {
    return null;
  }

  console.warn('Errors in form: ', errors);

  if (typeof errorMessage === 'string') {
    return (
      <DangerouslySetInnerHTML
        className={className}
        css={[tw`text-color-error my-2 mx-1/2e`]}
        markdownString={errorMessage}
      />
    );
  }

  return (
    <div className={className} css={[tw`text-color-error my-2 mx-1/2e`]}>
      {errorMessage}
    </div>
  );
};

Form.Status = ({ className }): JSX.Element | null => {
  const { formState } = useFormContext();
  const { isSubmitted, isDirty, isValid } = formState;
  const isSaved = !isDirty && isSubmitted && isValid;

  return (
    <div
      className={className}
      css={css`
        ${!isSaved && tw`opacity-0`}
        ${tw`flex items-center bg-blue-1 text-blue-3 rounded-full py-2 px-3 transition-all duration-500`}
      `}
    >
      <img
        alt='check'
        className='pr-2 w-6 h-6 text-blue-3'
        src={require('$/images/check.svg').default}
      />
      Changes saved!
    </div>
  );
};

Form.AccordionTitle = ({ alwaysOpen, title, children }): JSX.Element => {
  return (
    <div className='wrap-title text-xl'>
      <div className='flex items-center'>
        <span className='title'>{title}</span>
        {children}
      </div>
      {!alwaysOpen && <Icon name='angle right' />}
    </div>
  );
};

Form.StopNonHubspotForm = (): JSX.Element => {
  return (
    <>
      <label htmlFor='cc-num' className='hidden'>
        Stop Non-Hubspot Forms
      </label>
      <input name='cc-num' className='hidden' required={false} value='' id='cc-num'></input>
    </>
  );
};

Form.Link = PermissionedLink;

export default withProperties(Form, { SubmitMode, SubmitButton });
